import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Flex,
  Text,
  HStack,
  GridItem,
  Center,
  Spinner,
  Table,
  Tbody,
} from '@chakra-ui/react';
import Fuse from 'fuse.js';
import { Checkbox } from 'src/components/atoms/Checkbox';
import { Icon } from 'src/components/atoms/Icon';
import { SearchBox } from 'src/components/atoms/SearchBox';
import { Tabs, TabOption } from 'src/components/molecules/DataBox/Tabs';
import { InboxListItem } from 'src/features/Inbox/components/molecules/InboxListItem';
import { InboxMessageType } from 'src/api/inboxes';
import { useInboxesContext } from 'src/features/Inbox/contexts/InboxesListContext';
import { ErrorNotifBox } from 'src/components/atoms/ErrorNotifBox';
import { useCustomToast } from 'src/hooks/useCustomToast';
import { useBulkUpdateMessagesMutation } from 'src/api/inboxes';
import { useAppContext } from 'src/contexts/AppContext';

const ITEMS_PER_PAGE = 10;

const filterTabs: Array<TabOption<InboxMessageType>> = [
  { label: 'All messages', value: 'all' },
  { label: 'Sent', value: 'sent' },
  { label: 'System', value: 'system' },
  { label: 'Trash', value: 'trash' },
];

export const InboxesList: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const [selectedFilter, setSelectedFilter] = useState<InboxMessageType>('all');
  const { items, setItems, isLoading, isError } = useInboxesContext();
  const { showError } = useCustomToast();
  const { currentCustomer } = useAppContext();
  const { mutateAsync: bulkUpdateMessages, isLoading: isUpdatingMessages } =
    useBulkUpdateMessagesMutation(
      currentCustomer?.customerName ?? 'missing_customer',
    );
  // Filter items based on message type
  const typeFilteredItems = useMemo(() => {
    if (selectedFilter === 'all') {
      return items.filter((item) => !item.isTrash);
    }
    return items.filter((item) => {
      if (selectedFilter === 'trash') {
        return item.isTrash === true;
      }
      return item.messageType === selectedFilter;
    });
  }, [items, selectedFilter]);

  // Filter items based on search
  const filteredItems = useMemo(() => {
    if (searchValue.trim() === '') {
      return typeFilteredItems;
    }

    // Use Fuse.js to search in nested fields
    const fuse = new Fuse(typeFilteredItems, {
      keys: [
        { name: 'originator.name', weight: 0.3 },
        { name: 'title', weight: 0.4 },
        { name: 'description', weight: 0.3 },
      ],
      threshold: 0.2,
      distance: 1000,
    });

    // Map search results and ensure selection state is preserved
    const searchResults = fuse.search(searchValue).map((result) => result.item);

    // Create a map of original items by ID for quick lookup
    const itemsMap = new Map(items.map((item) => [item.id, item]));

    // Merge selection state from original items
    return searchResults.map((item) => {
      const originalItem = itemsMap.get(item.id);
      return originalItem
        ? { ...item, isSelected: originalItem.isSelected }
        : item;
    });
  }, [typeFilteredItems, searchValue, items]);

  const totalItems = filteredItems.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentPageItems = filteredItems.slice(startIndex, endIndex);

  const startItemNumber = startIndex + 1;
  const endItemNumber = Math.min(endIndex, totalItems);

  // Create a map of item IDs to their selection state for quick lookup
  const itemSelectionMap = useMemo(() => {
    const map = new Map<string, boolean>();
    items.forEach((item) => {
      map.set(item.id, item.isSelected || false);
    });
    return map;
  }, [items]);

  // Check if all items on current page are selected
  const areAllItemsSelected = useMemo(() => {
    return (
      currentPageItems.length > 0 &&
      currentPageItems.every((item) => itemSelectionMap.get(item.id))
    );
  }, [currentPageItems, itemSelectionMap]);

  // Check if some items on current page are selected
  const areSomeItemsSelected = useMemo(() => {
    return currentPageItems.some((item) => itemSelectionMap.get(item.id));
  }, [currentPageItems, itemSelectionMap]);

  // Reset to first page when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchValue, selectedFilter]);

  const handleToggleSelectAll = () => {
    setItems((prevItems) => {
      const shouldSelectAll = !areAllItemsSelected;
      const currentPageItemIds = new Set(
        currentPageItems.map((item) => item.id),
      );

      return prevItems.map((item) => {
        // Only toggle items on current page
        if (currentPageItemIds.has(item.id)) {
          return { ...item, isSelected: shouldSelectAll };
        }
        return item;
      });
    });
  };

  const handleToggleSelect = (id: string) => {
    setItems((prevItems) =>
      prevItems.map((item) =>
        item.id === id ? { ...item, isSelected: !item.isSelected } : item,
      ),
    );
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleBulkDelete = async () => {
    const selectedIds = currentPageItems
      .filter((item) => item.isSelected)
      .map((item) => item.id);

    try {
      await bulkUpdateMessages({
        ids: selectedIds,
        updates: { isTrash: true },
      });
      setItems((prevItems) =>
        prevItems.map((item) =>
          selectedIds.includes(item.id)
            ? { ...item, isTrash: true, isSelected: false }
            : item,
        ),
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error deleting message:', error);
      showError(
        'Error deleting message',
        'There was an error deleting the message',
      );
    }
  };

  if (isLoading || isUpdatingMessages) {
    return (
      <Center w="100%" h="100%">
        <Spinner speed="0.8s" size="xl" thickness="4px" />
      </Center>
    );
  }
  if (isError) {
    return (
      <Center w="100%" h="100%">
        <ErrorNotifBox
          title="Error loading inbox details!"
          description="There was an error loading the inbox details."
        />
      </Center>
    );
  }
  if (!items || items.length === 0) {
    return (
      <Center w="100%" h="100%">
        <Text fontFamily="marketingPage" fontSize="sm" color="text.title">
          No inbox messages found!
        </Text>
        <Text fontFamily="marketingPage" fontSize="sm" color="text.title">
          Create a new inbox message to get started.
        </Text>
      </Center>
    );
  }

  return (
    <GridItem colSpan={12}>
      <Box
        bg="surface.card"
        borderRadius={16}
        border="1px solid"
        borderColor="border.subtle"
        overflow="hidden"
        w="100%">
        {/* Search Box and Filters */}
        <Flex
          justifyContent="space-between"
          alignItems="center"
          px={6}
          pt={4}
          pb={2}
          gap={4}>
          <Box flex="1">
            <SearchBox
              value={searchValue}
              onChange={setSearchValue}
              maxWidth="440px"
            />
          </Box>
          <Box flex="1" display="flex" justifyContent="flex-end">
            <Tabs
              options={filterTabs}
              onSelect={(value) => setSelectedFilter(value)}
              selectedTab={selectedFilter}
            />
          </Box>
        </Flex>

        {/* Header with Select All and Pagination */}
        <Flex
          justifyContent="space-between"
          alignItems="center"
          px={6}
          py={4}
          borderBottom="1px solid"
          borderColor="border.subtle">
          <HStack>
            <Checkbox
              isChecked={areAllItemsSelected}
              isIndeterminate={areSomeItemsSelected && !areAllItemsSelected}
              onChange={handleToggleSelectAll}
              label="Select all"
              fontSize="14px"
              color="text.darkGray"
            />
            {areSomeItemsSelected && (
              <Icon iconName="trash" boxSize={5} onClick={handleBulkDelete} />
            )}
          </HStack>
          <HStack gap={2}>
            <Text fontFamily="marketingPage" fontSize="sm" color="text.title">
              {startItemNumber}-{endItemNumber} of {totalItems}
            </Text>
            <HStack gap={1}>
              <Box
                as="button"
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                cursor={currentPage === 1 ? 'not-allowed' : 'pointer'}
                opacity={currentPage === 1 ? 0.5 : 1}
                _hover={currentPage !== 1 ? { opacity: 0.8 } : {}}>
                <Icon iconName="chevron-left" boxSize={5} />
              </Box>
              <Box
                as="button"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                cursor={currentPage === totalPages ? 'not-allowed' : 'pointer'}
                opacity={currentPage === totalPages ? 0.5 : 1}
                _hover={currentPage !== totalPages ? { opacity: 0.8 } : {}}>
                <Icon iconName="chevron-right" boxSize={5} />
              </Box>
            </HStack>
          </HStack>
        </Flex>

        {/* List Items */}
        <Table variant="unstyled" w="100%">
          <Tbody>
            {currentPageItems.map((item) => (
              <InboxListItem
                key={item.id}
                item={item}
                onToggleSelect={handleToggleSelect}
              />
            ))}
          </Tbody>
        </Table>
      </Box>
    </GridItem>
  );
};
